import React, { useMemo, useEffect } from 'react';
import { connect } from 'react-redux';
import { isEmpty, isEqual } from 'lodash';
import { Table } from '../ui-kit';
import { BarLoader } from 'react-spinners';
import { operations as dutyTransactionOperations } from '../../state/modules/duty-transactions';
import dutyTransactionSelectors from '../../state/modules/duty-transactions/selectors';
import { operations as groupOperations } from '../../state/modules/groups';
import { operations as userOperations } from '../../state/modules/users';

import OverlappingDutyTransactionRow from './OverlappingDutyTransactionRow';

const OverlappingDutyTransactions = ({
  groupIds,
  userId,
  startTime,
  endTime,
  onlyOfType = ['duty_request', 'absence_request', 'duty_exchange', 'extra_work_request'],
  withHeader,
  dutyTransactions,
  dutyTransactionsLoaded,
  fetchDutyTransactions,
  fetchGroups,
  fetchUsers
}) => {
  useEffect(() => {
    if (isEmpty(groupIds)) return;

    let params = { group_ids: groupIds.join(',') };
    if (userId) params = { ...params, regarding_user_id: userId };
    fetchDutyTransactions({ startTime, endTime, params, forceFetch: true });
  }, [groupIds, userId, fetchDutyTransactions, startTime, endTime]);

  useEffect(() => {
    if (!isEmpty(groupIds)) fetchGroups({ params: { ids: groupIds } });
  }, [groupIds, fetchGroups]);

  useEffect(() => {
    const userIds = dutyTransactions
      .filter(dt => !dt.transient)
      .flatMap(dt => (dt.duty_exchange_intervals || []).map(interval => interval.user_id));

    if (!isEmpty(userIds)) {
      fetchUsers({ ids: userIds });
    }
  }, [dutyTransactions, fetchUsers]);

  // Make sure we only display the persisted ones
  const filteredDutyTransactions = useMemo(() => {
    let filtered = dutyTransactions.filter(dt => onlyOfType.includes(dt.type)).filter(dt => !dt.transient);

    if (!userId) return filtered;

    // Since the endpoint is additive, we still need to apply a filter
    // by user ID client side to ensure we don't show requests that were
    // previously loaded for other users
    filtered = filtered.filter(dt => dt.user_id === userId);

    return filtered;
  }, [dutyTransactions, onlyOfType, userId]);

  if (!dutyTransactionsLoaded) return <BarLoader height="4px" width="100%" color="#C30000" />;

  if (dutyTransactionsLoaded && filteredDutyTransactions.length === 0) {
    // If the table has a header, return nothing, becasue neither the header nor the table
    // will be shown. Showing a random no results found looks weird.
    // If the table has no header, we can assume the header is displayed elsewhere, and
    // can show a no results found message
    return withHeader ? null : <p>{I18n.t('no_results_found')}</p>;
  }

  const multiGroup = groupIds.length > 1;

  const showResponses = isEqual(onlyOfType, ['duty_request']);

  return (
    <>
      {withHeader && (
        <h4>{I18n.t('activerecord.attributes.schedule/exchanges/duty_request.overlapping_duty_requests')}</h4>
      )}
      <Table striped bordered condensed hover>
        <thead>
          <tr>
            <th>{I18n.t('attributes.type')}</th>
            <th>{I18n.t('activerecord.attributes.schedule/exchanges/duty_transaction.created_at')}</th>
            <th>{I18n.t('attributes.start_time')}</th>
            <th>{I18n.t('attributes.end_time')}</th>
            {multiGroup && <th>{I18n.t('activerecord.models.group')}</th>}
            {showResponses && (
              <th>
                {I18n.t('activerecord.attributes.schedule/exchanges/duty_request.candidate_names_and_response_icons')}
              </th>
            )}
            <th>{I18n.t('activerecord.attributes.schedule/exchanges/duty_transaction.status')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {filteredDutyTransactions.map(dt => (
            <OverlappingDutyTransactionRow key={dt.id} dt={dt} multiGroup={multiGroup} showResponses={showResponses} />
          ))}
        </tbody>
      </Table>
    </>
  );
};

const mapStateToProps = state => ({
  dutyTransactions: dutyTransactionSelectors.values(state),
  dutyTransactionsLoaded: dutyTransactionSelectors.selectInitialLoadComplete(state)
});

const mapDispatchToProps = {
  fetchDutyTransactions: dutyTransactionOperations.fetch,
  fetchUsers: userOperations.fetch,
  fetchGroups: groupOperations.fetch
};

export default connect(mapStateToProps, mapDispatchToProps)(OverlappingDutyTransactions);
